import {
  deviceEncryptionKeySchema,
  devicePlatformSchema,
  registerDeviceInputSchema,
  type DeviceEncryptionKey,
  type RegisterDeviceInput
} from "@memory-universe/domain";
import type { RuntimeProbe } from "../../../shared/platform/runtime";
import { supabase } from "../../../shared/supabase/client";

function labelForPlatform(platform: RegisterDeviceInput["platform"]) {
  if (platform === "windows") return "Windows 客户端";
  if (platform === "android") return "Android 客户端";
  return "浏览器设备";
}

export function registrationInputFromRuntime(userId: string, runtime: RuntimeProbe): RegisterDeviceInput | null {
  if (!runtime.storageReady || !runtime.deviceId) return null;
  const platform = devicePlatformSchema.safeParse(runtime.platform);
  if (!platform.success) return null;
  return registerDeviceInputSchema.parse({
    id: runtime.deviceId,
    userId,
    platform: platform.data,
    label: labelForPlatform(platform.data)
  });
}

async function provisionRuntimeEncryptionKey(runtime: RuntimeProbe): Promise<DeviceEncryptionKey | null> {
  if (!runtime.deviceId) return null;
  const result = runtime.kind === "tauri"
    ? await import("@tauri-apps/api/core").then(({ invoke }) => invoke<DeviceEncryptionKey>("provision_device_encryption_key"))
    : await import("../../../shared/platform/browserSecureMedia").then(({ provisionBrowserDeviceEncryptionKey }) => provisionBrowserDeviceEncryptionKey());
  const key = deviceEncryptionKeySchema.parse(result);
  if (key.deviceId !== runtime.deviceId) throw new Error("本机设备密钥与设备标识不匹配");
  return key;
}

const retirementChecked = new Set<string>();

function isUnavailable(error: { code?: string } | null) {
  return error?.code === "PGRST202" || error?.code === "PGRST205" || error?.code === "42P01";
}

/**
 * Browser identities are backed by IndexedDB. Older builds could leave a
 * record behind after a browser storage reset, causing it to remain a relay
 * recipient forever. The database only retires stale browser records and
 * never removes the local copy or its receipt history.
 */
async function retireStaleBrowserDevicesOnce(userId: string, runtime: RuntimeProbe) {
  if (runtime.platform !== "browser" || !runtime.deviceId || !supabase) return;
  const key = `${userId}:${runtime.deviceId}`;
  if (retirementChecked.has(key)) return;
  retirementChecked.add(key);
  const { error } = await supabase.rpc("retire_stale_browser_devices", {
    active_device_id: runtime.deviceId
  });
  if (error && !isUnavailable(error)) {
    // Device registration must remain available if maintenance is temporarily
    // unavailable. The next app start will retry this idempotent operation.
    retirementChecked.delete(key);
  }
}

export async function registerRuntimeDevice(userId: string, runtime: RuntimeProbe) {
  const input = registrationInputFromRuntime(userId, runtime);
  if (!input || !supabase) return false;

  const { error } = await supabase.from("device_registrations").upsert(
    {
      id: input.id,
      user_id: input.userId,
      platform: input.platform,
      device_label: input.label,
      last_seen_at: new Date().toISOString()
    },
    { onConflict: "id" }
  );
  if (error) {
    // Releases that predate migration 007 remain usable until their project is migrated.
    if (error.code === "PGRST205" || error.code === "42P01") return false;
    throw error;
  }

  const key = await provisionRuntimeEncryptionKey(runtime);
  if (!key) return true;
  const { data: existingKey, error: existingKeyError } = await supabase
    .from("device_encryption_keys")
    .select("id")
    .eq("device_id", key.deviceId)
    .eq("key_version", key.keyVersion)
    .maybeSingle();
  if (existingKeyError) {
    if (existingKeyError.code === "PGRST205" || existingKeyError.code === "42P01") return true;
    throw existingKeyError;
  }
  if (existingKey) {
    await retireStaleBrowserDevicesOnce(userId, runtime);
    return true;
  }

  const { error: keyError } = await supabase.from("device_encryption_keys").insert({
    device_id: key.deviceId,
    key_version: key.keyVersion,
    algorithm: key.algorithm,
    public_key: key.publicKey
  });
  if (keyError) throw keyError;
  await retireStaleBrowserDevicesOnce(userId, runtime);
  return true;
}
